import { Alert, Button, Flex, Form, Input, Select, Tabs, Upload, Typography } from 'antd';
import type { TabsProps } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { AssetUploadProgress } from '@/components/common/AssetUploadProgress';
import { TagManager } from '@/components/common/TagManager';
import {
  CrepeMarkdownField,
  type CrepeMarkdownFieldHandle,
} from '@/components/editor/CrepeMarkdownField';
import { useAddAsset } from '@/hooks/useAddAsset';
import { generateUuidV4, isValidUuid } from '@/utils/uuid';

const { Paragraph, Text } = Typography;

/** The active tab in the Add Asset form: link URL, file upload, or inline note. */
type AddAssetIntent = 'link' | 'file' | 'note';
/** Requested AI summary depth for uploaded documents and linked URLs. */
type SummaryStyle = 'concise' | 'detailed';

export const ADD_ASSET_SUPPORTED_UPLOAD_TYPES = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv';

/** Returns `true` when the input is a valid HTTP or HTTPS URL. */
const isHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/** Returns the initial blank form state used when resetting the Add Asset form. */
const resetAddAssetFormState = (): {
  intent: AddAssetIntent;
  linkUrl: string;
  fileList: UploadFile[];
  summaryStyle: SummaryStyle;
  uploading: boolean;
  uploadPhase: string | null;
  uploadProgress: number | null;
} => ({
  intent: 'file',
  linkUrl: '',
  fileList: [],
  summaryStyle: 'concise',
  uploading: false,
  uploadPhase: null,
  uploadProgress: null,
});

/** Flags computed from current form state that drive UI affordances (button enabled, error visibility, etc.). */
interface AddAssetDerivedState {
  canCreate: boolean;
  canSaveInlineNote: boolean;
  canUploadDocument: boolean;
  fileStaged: boolean;
  isSupportedDocument: boolean;
  isTabularDocument: boolean;
  selectedFile?: File;
  showLinkError: boolean;
  showSummaryStyle: boolean;
  wiredUpload: boolean;
}

/** Derives validation and UI-state flags from the current form inputs and prop configuration. */
function deriveAddAssetState({
  accessToken,
  clientId,
  createNoteInline,
  fileList,
  inlineNoteMarkdown,
  intent,
  linkUrl,
  noteTitle,
  onWordDocumentUploaded,
  projectId,
  sharepointReady,
}: Pick<
  AddAssetOptionsFormProps,
  | 'accessToken'
  | 'clientId'
  | 'createNoteInline'
  | 'onWordDocumentUploaded'
  | 'projectId'
  | 'sharepointReady'
> & {
  fileList: UploadFile[];
  inlineNoteMarkdown: string;
  intent: AddAssetIntent;
  linkUrl: string;
  noteTitle: string;
}): AddAssetDerivedState {
  const trimmedLinkUrl = linkUrl.trim();
  const selected = fileList[0]?.originFileObj;
  const selectedFile = selected instanceof File ? selected : undefined;
  const fileName = selectedFile?.name.toLowerCase() ?? '';
  const isSupportedDocument = ['.pdf', '.docx', '.pptx', '.xlsx', '.csv'].some((extension) =>
    fileName.endsWith(extension),
  );
  const isTabularDocument = fileName.endsWith('.xlsx') || fileName.endsWith('.csv');
  const fileStaged = intent === 'file' && fileList.length === 1;
  const wiredUpload = Boolean(onWordDocumentUploaded && accessToken && clientId && projectId);
  const canUploadDocument =
    fileStaged && isSupportedDocument && wiredUpload && sharepointReady === true;
  const canSaveInlineNote = Boolean(
    createNoteInline &&
    intent === 'note' &&
    noteTitle.trim() &&
    inlineNoteMarkdown.trim() &&
    accessToken &&
    clientId,
  );
  const isLinkValid = intent === 'link' && Boolean(trimmedLinkUrl) && isHttpUrl(trimmedLinkUrl);
  return {
    canCreate:
      isLinkValid || canUploadDocument || (fileStaged && !wiredUpload) || canSaveInlineNote,
    canSaveInlineNote,
    canUploadDocument,
    fileStaged,
    isSupportedDocument,
    isTabularDocument,
    selectedFile,
    showLinkError: intent === 'link' && Boolean(trimmedLinkUrl) && !isHttpUrl(trimmedLinkUrl),
    showSummaryStyle: intent === 'link' || intent === 'file',
    wiredUpload,
  };
}

/** Props for the `InlineNoteTabContent` sub-component rendered on the "Create note" tab. */
interface InlineNoteTabContentProps {
  accessToken: string | null;
  clientId?: string;
  createNoteInline: boolean;
  inlineNoteEditorRef: React.Ref<CrepeMarkdownFieldHandle>;
  inlineNoteMarkdown: string;
  intent: AddAssetIntent;
  noteDraftId: string;
  noteTags: string[];
  noteTitle: string;
  projectId?: string;
  setInlineNoteMarkdown: (value: string) => void;
  setNoteTags: (value: string[]) => void;
  setNoteTitle: (value: string) => void;
}

/** Renders the inline note editor (title, tags, Crepe body) when `createNoteInline` is true and the note tab is active. */
function InlineNoteTabContent({
  accessToken,
  clientId,
  createNoteInline,
  inlineNoteEditorRef,
  inlineNoteMarkdown,
  intent,
  noteDraftId,
  noteTags,
  noteTitle,
  projectId,
  setInlineNoteMarkdown,
  setNoteTags,
  setNoteTitle,
}: Readonly<InlineNoteTabContentProps>): React.ReactElement {
  if (!createNoteInline) {
    return <Text type="secondary">Selecting this tab will open the Create Note route.</Text>;
  }

  if (intent !== 'note') {
    return (
      <Text type="secondary">
        Select this tab to open the inline editor. It loads only while you are on this tab so the
        rest of the page stays responsive.
      </Text>
    );
  }

  return (
    <div data-testid="add-asset-inline-create-note">
      <input
        aria-label="Inline note title"
        className="create-note-title-input"
        placeholder="Enter title..."
        value={noteTitle}
        onChange={(event) => setNoteTitle(event.target.value)}
        style={{ marginBottom: 12 }}
      />
      {accessToken && clientId ? (
        <div style={{ marginBottom: 12 }}>
          <TagManager
            tags={noteTags}
            accessToken={accessToken}
            clientId={clientId}
            projectId={projectId}
            cardId={isValidUuid(noteDraftId) ? noteDraftId : null}
            localOnly
            onTagsChange={setNoteTags}
          />
        </div>
      ) : null}
      <div className="create-note-page__editor-region">
        <CrepeMarkdownField
          ref={inlineNoteEditorRef}
          bootKey={noteDraftId}
          defaultMarkdown={inlineNoteMarkdown}
          onMarkdownChange={setInlineNoteMarkdown}
          minHeight={240}
        />
      </div>
    </div>
  );
}

/** Props for the `AddAssetOptionsForm` tabbed asset-addition form. */
export interface AddAssetOptionsFormProps {
  /** Modal: pass `open` — form resets when the modal opens. */
  active?: boolean;
  /** Embedded: reset when this identity changes (e.g. assessment instance id). */
  resetIdentityKey?: string;
  onCancel: () => void;
  /** Called where the Add Asset modal previously called `onClose` (link/create stub, upload success, before note navigation). */
  onActionComplete?: () => void;
  /**
   * When true, the Create note tab stays on this page with an inline editor + save instead of
   * navigating away (Add Asset modal should omit this or leave false).
   */
  createNoteInline?: boolean;
  /** Used when `createNoteInline` is false: opens the Create Note route with a new session id. */
  onCreateNoteNavigate?: (assetSessionId: string) => void;
  /** Optional hook after an inline note is saved successfully (receives server `card_id`). */
  onInlineNoteSaved?: (cardId: string) => void;
  sharepointReady?: boolean | null;
  accessToken?: string | null;
  microsoftOboAssertion?: string | null;
  clientId?: string;
  projectId?: string;
  onMicrosoftReauth?: () => Promise<void> | void;
  onMicrosoftRefreshObo?: () => Promise<string | null>;
  onWordDocumentUploaded?: (cardId: string) => void;
  /** When provided, a "Browse Existing" tab is added; clicking it calls this callback immediately. */
  onBrowseAssets?: () => void;
  /** Override default intro copy under the title. */
  intro?: React.ReactNode;
}

/** Tabbed form for adding a new knowledge card asset via URL link, file upload, inline note, or browse. */
export const AddAssetOptionsForm: React.FC<AddAssetOptionsFormProps> = ({
  active,
  resetIdentityKey,
  onCancel,
  onActionComplete,
  createNoteInline = false,
  onCreateNoteNavigate,
  onInlineNoteSaved,
  sharepointReady = null,
  accessToken = null,
  microsoftOboAssertion = null,
  clientId,
  projectId,
  onMicrosoftReauth,
  onMicrosoftRefreshObo,
  onWordDocumentUploaded,
  onBrowseAssets,
  intro,
}: Readonly<AddAssetOptionsFormProps>) => {
  const [intent, setIntent] = useState<AddAssetIntent>('file');
  const [linkUrl, setLinkUrl] = useState<string>('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [summaryStyle, setSummaryStyle] = useState<SummaryStyle>('concise');
  const { uploading, uploadPhase, uploadProgress, uploadDocument, saveInlineNote } = useAddAsset(
    accessToken,
    clientId,
    projectId,
    microsoftOboAssertion,
    onMicrosoftRefreshObo,
    onMicrosoftReauth,
  );

  const [noteDraftId, setNoteDraftId] = useState(() => generateUuidV4());
  const [noteTitle, setNoteTitle] = useState('');
  const [noteTags, setNoteTags] = useState<string[]>([]);
  const [inlineNoteMarkdown, setInlineNoteMarkdown] = useState('');
  const inlineNoteEditorRef = useRef<CrepeMarkdownFieldHandle | null>(null);

  const applyReset = useCallback(() => {
    const next = resetAddAssetFormState();
    setIntent(next.intent);
    setLinkUrl(next.linkUrl);
    setFileList(next.fileList);
    setSummaryStyle(next.summaryStyle);
    setNoteTitle('');
    setNoteTags([]);
    setInlineNoteMarkdown('');
    setNoteDraftId(generateUuidV4());
  }, []);

  useEffect(() => {
    if (active !== undefined) {
      if (!active) {
        return;
      }
      applyReset();
      return;
    }
    if (resetIdentityKey !== undefined) {
      applyReset();
    }
  }, [active, resetIdentityKey, applyReset]);

  const {
    canCreate,
    canSaveInlineNote,
    canUploadDocument,
    fileStaged,
    isSupportedDocument,
    isTabularDocument,
    selectedFile,
    showLinkError,
    showSummaryStyle,
    wiredUpload,
  } = deriveAddAssetState({
    accessToken,
    clientId,
    createNoteInline,
    fileList,
    inlineNoteMarkdown,
    intent,
    linkUrl,
    noteTitle,
    onWordDocumentUploaded,
    projectId,
    sharepointReady,
  });

  const finishAction = (): void => {
    onActionComplete?.();
  };

  /** Saves the inline note — wires through the inline editor ref for the live markdown. */
  const handleSaveInlineNote = async (): Promise<void> => {
    if (!canSaveInlineNote) {
      return;
    }
    const latestMarkdown =
      inlineNoteEditorRef.current?.getMarkdown().trim() ?? inlineNoteMarkdown.trim();
    await saveInlineNote(
      { noteTitle, noteDraftId, noteTags, latestMarkdown, projectId },
      (cardId) => {
        onInlineNoteSaved?.(cardId);
        applyReset();
      },
      finishAction,
    );
  };

  /** Primary action handler: routes to inline-note save, document upload, or link creation based on current intent. */
  const handleCreate = (): void => {
    if (createNoteInline && intent === 'note') {
      void handleSaveInlineNote();
      return;
    }
    if (intent === 'file' && wiredUpload) {
      if (!canUploadDocument || !(selectedFile instanceof File)) {
        return;
      }
      if (!onWordDocumentUploaded) {
        return;
      }
      void uploadDocument(selectedFile, summaryStyle, onWordDocumentUploaded, finishAction);
      return;
    }

    if (!canCreate) {
      return;
    }
    finishAction();
    applyReset();
  };

  /** Generates a new session ID and navigates to the Create Note route when `createNoteInline` is false. */
  const handleCreateNote = (): void => {
    const navigate = onCreateNoteNavigate;
    if (!navigate) {
      return;
    }
    const assetSessionId = generateUuidV4();
    finishAction();
    navigate(assetSessionId);
  };

  /** Switches intent when a tab is clicked; immediately fires `onBrowseAssets` or `onCreateNoteNavigate` for tabs that exit the form. */
  const handleTabChange = (key: string): void => {
    if (key === 'browse') {
      onBrowseAssets?.();
      return;
    }
    const nextIntent = key as AddAssetIntent;
    if (nextIntent === 'note' && !createNoteInline) {
      handleCreateNote();
      return;
    }
    setIntent(nextIntent);
  };

  /** Resets the form state and calls `onCancel`. */
  const handleFooterCancel = (): void => {
    applyReset();
    onCancel();
  };

  const inlineNoteTabChildren = (
    <InlineNoteTabContent
      accessToken={accessToken}
      clientId={clientId}
      createNoteInline={createNoteInline}
      inlineNoteEditorRef={inlineNoteEditorRef}
      inlineNoteMarkdown={inlineNoteMarkdown}
      intent={intent}
      noteDraftId={noteDraftId}
      noteTags={noteTags}
      noteTitle={noteTitle}
      projectId={projectId}
      setInlineNoteMarkdown={setInlineNoteMarkdown}
      setNoteTags={setNoteTags}
      setNoteTitle={setNoteTitle}
    />
  );

  const tabItems: TabsProps['items'] = [
    {
      key: 'file',
      label: 'Upload File',
      children: (
        <Form.Item style={{ marginBottom: 0 }}>
          {intent === 'file' && sharepointReady !== true && wiredUpload ? (
            <Alert
              type="warning"
              showIcon
              message="SharePoint not configured"
              description="Microsoft 365 / SharePoint is not configured for this project. PDF, Word, PowerPoint, Excel, and CSV files cannot be saved to your project folder or linked for later reading. Configure a SharePoint folder in Edit project, then try again."
              style={{ marginBottom: 12 }}
            />
          ) : null}
          <Upload.Dragger
            accept={ADD_ASSET_SUPPORTED_UPLOAD_TYPES}
            beforeUpload={() => false}
            multiple={false}
            maxCount={1}
            fileList={fileList}
            onChange={({ fileList: nextFileList }) => setFileList(nextFileList.slice(-1))}
            onRemove={() => {
              setFileList([]);
              return true;
            }}
            disabled={uploading}
            style={{ padding: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                style={{ color: 'var(--text-3)', display: 'inline-block' }}
              >
                <path
                  d="M12 16V4M12 4l-4 4M12 4l4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </p>
            <p className="ant-upload-text">Drop file here or browse</p>
            <p className="ant-upload-hint">PDF · DOCX · PPTX · XLSX · CSV — max 10 MB</p>
          </Upload.Dragger>
          {intent === 'file' && fileStaged && isTabularDocument ? (
            <Alert
              type="warning"
              showIcon
              message="Data safety"
              description="Do not upload PI, PII, or PHI. Remove personal identifiers before uploading. Uploads are extracted and indexed for AI search and summarization."
              style={{ marginTop: 12 }}
            />
          ) : null}

          {intent === 'file' && fileStaged && wiredUpload && !isSupportedDocument ? (
            <Paragraph type="danger" style={{ marginTop: 8, marginBottom: 0 }}>
              Project document upload currently supports .pdf, .docx, .pptx, .xlsx, and .csv only.
            </Paragraph>
          ) : null}
        </Form.Item>
      ),
    },
    {
      key: 'note',
      label: 'Create Note',
      children: inlineNoteTabChildren,
    },
    {
      key: 'link',
      label: 'Add Link',
      disabled: true,
      children: (
        <Form.Item
          label="URL"
          validateStatus={showLinkError ? 'error' : undefined}
          help={
            showLinkError ? 'Enter a valid URL that starts with http:// or https://.' : undefined
          }
          style={{ marginBottom: 0 }}
        >
          <Input
            value={linkUrl}
            onChange={(event) => setLinkUrl(event.target.value)}
            placeholder="https://example.com/resource"
            aria-label="Asset URL"
          />
        </Form.Item>
      ),
    },
    ...(onBrowseAssets
      ? [
          {
            key: 'browse',
            label: 'Browse Existing',
            children: (
              <Text type="secondary">Selecting this tab opens the project asset library.</Text>
            ),
          },
        ]
      : []),
  ];

  const defaultIntro = (
    <Paragraph style={{ marginBottom: 16 }}>
      Save documents, PDFs, URLs, or create a note.
    </Paragraph>
  );

  return (
    <>
      {intro === undefined ? defaultIntro : intro}

      {uploading ? (
        <AssetUploadProgress
          phase={uploadPhase}
          progress={uploadProgress}
          filename={selectedFile?.name}
        />
      ) : (
        <Form layout="vertical" requiredMark={false}>
          <Tabs activeKey={intent} onChange={handleTabChange} items={tabItems} />

          {showSummaryStyle ? (
            <div className="add-asset-modal__summary-row">
              <Form.Item className="add-asset-modal__summary-style">
                <Select
                  value={summaryStyle}
                  onChange={(value) => setSummaryStyle(value as SummaryStyle)}
                  aria-label="Summary style"
                  options={[
                    { label: 'Concise summary', value: 'concise' },
                    { label: 'Detailed summary', value: 'detailed' },
                  ]}
                />
              </Form.Item>
            </div>
          ) : null}
        </Form>
      )}

      <Flex justify="flex-end" gap="small" style={{ marginTop: 16 }}>
        {intent !== 'file' && (
          <Button onClick={handleFooterCancel} disabled={uploading}>
            Cancel
          </Button>
        )}
        <Button
          type="primary"
          loading={uploading}
          disabled={uploading || !canCreate}
          onClick={handleCreate}
        >
          {createNoteInline && intent === 'note' ? 'Save note' : 'Create'}
        </Button>
      </Flex>
    </>
  );
};

export default AddAssetOptionsForm;
